// import

import type {UserProfile} from '@auth0/nextjs-auth0';
import {withErrorBoundary} from '@sentry/nextjs';
import {withProfiler} from '@sentry/react';
import {isProd, isVercelPreview} from '@sitearcade/is-env';
import type {NextComponentType} from 'next';
import type {AppProps, NextWebVitalsMetric} from 'next/app';
import NextError from 'next/error';
import Head from 'next/head';
import {Fragment} from 'react';

import type {Settings} from 'libs/model';
import {initTracking} from 'libs/tracking';
import {SettingsProvider} from 'modules/App';
import {UserProvider, PageAuth} from 'modules/Auth';
import {ContactModal, ContactFormProvider} from 'modules/Contact';
import {GoogleTagManager} from 'modules/GoogleTagManager';
import type {WithLayout} from 'modules/Layout';
import {getDefaultLayout} from 'modules/Layout';
import {LoadingView} from 'modules/LoadingView';
import {MessageList, MessageListProvider} from 'modules/MessageList';
import {ModalsProvider} from 'modules/Modals';
import {RecaptchaScript} from 'modules/Recaptcha';
import {AppSeo} from 'modules/Seo';
import {GlobalStyles, ThemeProvider} from 'modules/Theme';
import {GlobalTypeStyles} from 'modules/Type';
import settings from 'preval/settings.json';

// types

type Props = AppProps & {
  Component: NextComponentType & {withLayout?: WithLayout};
  pageProps: {
    [index: string]: unknown;
    user?: UserProfile;
  };
};

// vars

const MaybePageAuth = isVercelPreview ? PageAuth : Fragment;

const postFeeds = ['rss.xml', 'atom.xml', 'json'].map((fmt) => (
  <link
    key={fmt}
    href={`/feed/post.${fmt}`}
    rel="alternate"
    title={`Blog Posts ⬩ SiteArcade ⬩ ${fmt.split('.')[0].toUpperCase()}`}
    type={`application/${fmt.replace('.', '+')}`}
  />
));

// config

initTracking();

// component

export default isProd ?
  withProfiler(withErrorBoundary(App as any, {fallback: NextError})) :
  App;

function App(props: Props) {
  const {Component, pageProps: {user, ...pageProps}} = props;
  const withLayout = Component.withLayout || getDefaultLayout();

  return (
    <UserProvider user={user}>
      <SettingsProvider value={settings as unknown as Settings}>
        <Head>
          {/* meta */}
          <meta content="SiteArcade" name="application-name" />
          <meta content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=3" name="viewport" />

          {/* feeds */}
          {postFeeds}
        </Head>

        <AppSeo />
        <RecaptchaScript />

        <ThemeProvider>
          <GlobalStyles />
          <GlobalTypeStyles />

          <MaybePageAuth>
            <MessageListProvider>
              <ContactFormProvider>
                <ModalsProvider>
                  {withLayout(
                    <Component {...pageProps} />,
                  )}
                </ModalsProvider>
                <ContactModal />
              </ContactFormProvider>

              <MessageList />
            </MessageListProvider>

            <LoadingView />
          </MaybePageAuth>

          <GoogleTagManager />
        </ThemeProvider>
      </SettingsProvider>
    </UserProvider>
  );
}

// data

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (!globalThis.dataLayer) {
    globalThis.dataLayer = [];
  }

  globalThis.dataLayer.push({
    event: 'metric',
    metricCategory: metric.label,
    metricName: metric.name,
    metricId: metric.id,
    metricValue: Math.round(metric.value * 1000),
  });
}
